import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';
import { Breadcrumb } from '@/components/feature/Footer';

interface DramaDetailProps {
  drama: Drama;
  related: Drama[];
}

export default function DramaDetail({ drama, related }: DramaDetailProps) {
  const [activeEpisode, setActiveEpisode] = useState(0);
  const [shareTip, setShareTip] = useState('');
  const episode = drama.episodes[activeEpisode];

  const handleShare = (platform: string) => {
    setShareTip(`已生成${platform}分享示意（演示功能，暂未实际分享）`);
    window.setTimeout(() => setShareTip(''), 3200);
  };

  return (
    <>
      <section className="relative w-full bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={drama.backdrop}
            alt={`${drama.title} 剧照`}
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent opacity-90"></div>
        </div>

        <div className="relative z-10 px-4 md:px-10 pt-6 pb-8 md:pb-10">
          <div className="[&_a]:text-background-400 [&_a:hover]:text-background-50 [&_span]:text-background-300 [&_i]:text-background-600 mb-6">
            <Breadcrumb
              items={[{ label: '首页', to: '/' }, { label: '伦理剧集', to: '/category/ethics' }, { label: drama.title }]}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="shrink-0 w-[180px] sm:w-[220px] md:w-[260px] mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-50/15 bg-primary-900">
                <img
                  src={drama.poster}
                  alt={`${drama.title} 海报`}
                  title={`${drama.title} 伦理剧`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md bg-background-50/10 px-3 h-9">
                <span className="text-[11px] text-background-300">站内演示评分</span>
                <span className="flex items-center gap-1 text-accent-400 text-sm font-medium">
                  <i className="ri-star-fill text-xs"></i>
                  {drama.rating.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0 pb-2">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="h-6 px-2.5 rounded-md bg-accent-500 text-primary-950 text-[11px] font-medium flex items-center">
                  {drama.status}
                </span>
                {drama.isClassic && (
                  <span className="h-6 px-2.5 rounded-md border border-accent-500/50 text-accent-300 text-[11px] flex items-center gap-1">
                    <i className="ri-award-line text-xs"></i>
                    经典佳作
                  </span>
                )}
                {drama.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="h-6 px-2.5 rounded-md border border-background-50/25 text-background-200 text-[11px] flex items-center hover:border-accent-400 hover:text-accent-300 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-[40px] leading-tight font-semibold text-background-50 mb-3">
                {drama.title}
              </h1>
              <p className="text-sm md:text-[15px] leading-7 text-background-200 max-w-3xl mb-5">
                {drama.summary}
              </p>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl mb-6">
                {[
                  { label: '导演', value: drama.director },
                  { label: '编剧', value: drama.writer },
                  { label: '主演', value: drama.cast.join(' / ') },
                  { label: '年份 / 地区', value: `${drama.year} · ${drama.region}` },
                  { label: '集数', value: `共 ${drama.episodes.length} 集（演示）` },
                  { label: '上线日期', value: drama.updateDate },
                ].map((item) => (
                  <div key={item.label} className="flex items-baseline gap-2 text-xs md:text-[13px]">
                    <dt className="text-background-400 shrink-0 w-16">{item.label}</dt>
                    <dd className="text-background-100 min-w-0">{item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex flex-wrap items-center gap-2.5">
                <a
                  href="#episodes"
                  className="h-10 md:h-11 px-5 md:px-6 rounded-md bg-accent-500 text-primary-950 text-xs md:text-sm font-medium flex items-center gap-2 whitespace-nowrap hover:bg-accent-400 transition-colors"
                >
                  <i className="ri-list-ordered"></i>
                  查看分集简介
                </a>
                <Link
                  to="/category/ethics"
                  className="h-10 md:h-11 px-5 md:px-6 rounded-md border border-background-50/30 text-background-100 text-xs md:text-sm flex items-center gap-2 whitespace-nowrap hover:bg-background-50/10 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回分类
                </Link>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-background-400 mr-1">分享：</span>
                {[
                  { key: '微信', icon: 'ri-wechat-line' },
                  { key: '微博', icon: 'ri-weibo-line' },
                  { key: '复制链接', icon: 'ri-links-line' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleShare(item.key)}
                    className="h-8 px-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-200 text-[11px] flex items-center gap-1.5 whitespace-nowrap hover:bg-background-50/20 transition-colors cursor-pointer"
                  >
                    <i className={`${item.icon} text-sm`}></i>
                    {item.key}
                  </button>
                ))}
              </div>
              {shareTip && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-accent-300 bg-primary-900/60 px-2.5 py-1.5 rounded-md">
                  <i className="ri-information-line"></i>
                  {shareTip}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-10 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
          <div className="flex-1 min-w-0">
            <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7 mb-6 md:mb-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-primary-600 rounded-full"></span>
                剧情简介
              </h2>
              <p className="text-sm leading-8 text-foreground-700 whitespace-pre-line">{drama.synopsis}</p>
              <div className="mt-5 pt-5 border-t border-background-200 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
                <span>类型：{drama.tags.join(' / ')}</span>
                <span>地区：{drama.region}</span>
                <span>年份：{drama.year}</span>
                <span>状态：{drama.status}</span>
              </div>
            </article>

            <section id="episodes" className="rounded-lg border border-background-200 bg-background-50 overflow-hidden mb-6 md:mb-8">
              <div className="px-5 md:px-7 pt-5 md:pt-6 pb-4 border-b border-background-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                  <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-4 bg-primary-600 rounded-full"></span>
                    分集简介
                  </h2>
                  <span className="text-[11px] text-foreground-400">
                    共 {drama.episodes.length} 集 · 章节内容为演示文字
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mt-1">
                  点击下方章节切换查看该集梗概，仅提供文字介绍，不含播放内容。
                </p>
              </div>

              <div className="px-5 md:px-7 py-4 border-b border-background-200">
                <div className="flex flex-wrap gap-1.5">
                  {drama.episodes.map((item, idx) => (
                    <button
                      key={item.no}
                      type="button"
                      onClick={() => setActiveEpisode(idx)}
                      aria-current={idx === activeEpisode ? 'true' : undefined}
                      className={`h-9 min-w-[44px] px-3 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                        idx === activeEpisode
                          ? 'bg-primary-700 text-background-50'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      第{String(item.no).padStart(2, '0')}集
                    </button>
                  ))}
                </div>
              </div>

              <div className="px-5 md:px-7 py-5 md:py-6" key={episode.no}>
                <div className="flex items-start gap-3 md:gap-4 animate-fade-up">
                  <span className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-md bg-accent-100 text-accent-900 flex flex-col items-center justify-center leading-none">
                    <span className="text-[9px]">EP</span>
                    <span className="text-sm font-medium mt-0.5">{String(episode.no).padStart(2, '0')}</span>
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                      第{episode.no}集 · {episode.title}
                    </h3>
                    <p className="text-sm leading-7 text-foreground-600">{episode.synopsis}</p>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    disabled={activeEpisode === 0}
                    onClick={() => setActiveEpisode((v) => Math.max(0, v - 1))}
                    className="h-9 px-3.5 rounded-md border border-background-300 text-xs text-foreground-700 flex items-center gap-1 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    上一集
                  </button>
                  <span className="text-[11px] text-foreground-400">
                    {activeEpisode + 1} / {drama.episodes.length}
                  </span>
                  <button
                    type="button"
                    disabled={activeEpisode === drama.episodes.length - 1}
                    onClick={() => setActiveEpisode((v) => Math.min(drama.episodes.length - 1, v + 1))}
                    className="h-9 px-3.5 rounded-md border border-background-300 text-xs text-foreground-700 flex items-center gap-1 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer"
                  >
                    下一集
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100/60 px-5 md:px-7 py-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2">浏览提示</h2>
              <p className="text-xs md:text-sm leading-7 text-foreground-600">
                本站仅提供影视作品的资料整理与剧情介绍，不提供在线播放与下载服务。页面中的分集梗概、评分与热度均为站内演示数据，影视作品的观看请通过正规授权渠道进行。
              </p>
            </section>
          </div>

          <aside className="w-full lg:w-[320px] shrink-0">
            <div className="lg:sticky lg:top-24 space-y-5">
              <section className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
                  <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-magic-line text-primary-600"></i>
                    相关推荐
                  </h2>
                  <span className="text-[11px] text-foreground-400">按标签关联</span>
                </div>
                <ul className="divide-y divide-background-200">
                  {related.map((item, idx) => (
                    <li key={item.id}>
                      <Link to={`/detail/${item.id}`} className="group flex gap-3 p-3.5 hover:bg-background-100/70 transition-colors">
                        <div className="shrink-0 w-[62px] h-[86px] rounded-md overflow-hidden bg-background-200">
                          <img
                            src={item.poster}
                            alt={`${item.title} 海报`}
                            title={`${item.title} 伦理剧`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-primary-500 font-medium">{String(idx + 1).padStart(2, '0')}</span>
                            <h3 className="text-[13px] font-medium text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
                              {item.title}
                            </h3>
                          </div>
                          <p className="text-[11px] text-foreground-500 mt-1">
                            {item.year} · {item.tags.slice(0, 2).join(' / ')}
                          </p>
                          <p className="text-[11px] leading-5 text-foreground-600 line-clamp-2 mt-1.5">
                            {item.summary}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-lg border border-background-200 bg-accent-50 px-5 py-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                  <i className="ri-quill-pen-line text-accent-700"></i>
                  主创信息
                </h2>
                <dl className="space-y-2.5 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500">导演</dt>
                    <dd className="text-foreground-900 text-right">{drama.director}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500">编剧</dt>
                    <dd className="text-foreground-900 text-right">{drama.writer}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-foreground-500">主演</dt>
                    <dd className="text-foreground-900 text-right">{drama.cast.join(' / ')}</dd>
                  </div>
                </dl>
                <Link
                  to="/contact"
                  className="mt-4 h-9 w-full rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 flex items-center justify-center gap-1.5 whitespace-nowrap hover:border-primary-300 transition-colors"
                >
                  内容纠错与反馈
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}